import React, { useCallback, useEffect, useState } from 'react';
import { aaiApi } from '../../../services/aaiApi';
import type { AaiHome } from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import ParticipantHome from './ParticipantHome';
import DeptHeadHome from './DeptHeadHome';
import ChampionHome from './ChampionHome';
import ConsultantHome from './ConsultantHome';
import AdminHome from './AdminHome';
import ExpertHome from './ExpertHome';

// Fetches GET /api/aai/home and renders the designed dashboard for the caller's
// role. One component, role-gated — the whole point of the imported design.
const RoleDashboard: React.FC = () => {
  const [home, setHome] = useState<AaiHome | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await aaiApi.getHome();
      setHome(data);
    } catch {
      setError('Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading label="Loading your dashboard…" />;
  if (error || !home) return <ErrorNote message={error || 'Something went wrong.'} />;

  switch (home.role) {
    case 'participant': return <ParticipantHome data={home} />;
    case 'department_head': return <DeptHeadHome data={home} onRefresh={load} />;
    case 'champion': return <ChampionHome data={home} onRefresh={load} />;
    case 'consultant': return <ConsultantHome data={home} />;
    case 'owner': return <AdminHome data={home} onRefresh={load} />;
    case 'expert': return <ExpertHome data={home} />;
    default: return <ErrorNote message="No AI Adoption role for this workspace." />;
  }
};

export default RoleDashboard;
